"use client";

import { Fragment, isValidElement, type ReactNode, useCallback, useEffect, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Icon } from "../Icon";
import { Popover } from "../Popover";
import { TextField } from "../TextField";
import { Tray } from "../Tray";

export interface CommandPaletteProps {
  /**
   * Placeholder text for the search input
   * @default "Search..."
   */
  placeholder?: string;
  /**
   * Value of the search input (controlled)
   */
  value?: string;
  /**
   * Handler for when the search input changes
   */
  onChange?: (value: string) => void;
  /**
   * Whether the command palette is open (controlled)
   */
  isOpen?: boolean;
  /**
   * Handler for when the command palette is opened/closed
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Custom trigger button (if not provided, uses default search button)
   * Pass null or empty fragment to render headless (no trigger, controlled externally)
   */
  trigger?: ReactNode;
  /**
   * Content to display in the results area
   */
  children: ReactNode;
  /**
   * Whether to show the keyboard shortcut hint
   * @default true
   */
  showShortcutHint?: boolean;
  /**
   * Whether to show the search input inside the popover
   * Set to false when using SearchTrigger externally
   * @default true
   */
  showInput?: boolean;
  /**
   * Additional CSS classes for the input
   */
  className?: string;
}

/**
 * CommandPalette
 *
 * Global search command palette with keyboard shortcuts (CMD/CTRL+K).
 * Provides a desktop popover and mobile full-screen experience.
 *
 * Desktop: Shows as a popover with search input at top
 * Mobile: Shows as full-screen tray
 *
 * @example
 * ```tsx
 * const [query, setQuery] = useState("");
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <CommandPalette
 *   value={query}
 *   onChange={setQuery}
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 * >
 *   <SearchResultsList>
 *     <SearchResultGroup title="Sites" count={2}>
 *       <SearchResultItem id="1" title="HQ" />
 *     </SearchResultGroup>
 *   </SearchResultsList>
 * </CommandPalette>
 * ```
 */
export function CommandPalette({
  placeholder = "Search...",
  value,
  onChange,
  isOpen: controlledIsOpen,
  onOpenChange,
  trigger,
  children,
  showShortcutHint = true,
  showInput = true,
  className,
}: CommandPaletteProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Use controlled state if provided, otherwise use uncontrolled state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setUncontrolledIsOpen(open);
      }
    },
    [onOpenChange]
  );

  // Handle CMD/CTRL+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      // Escape to close
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue);
    },
    [onChange]
  );

  const defaultTrigger = (
    <button
      type="button"
      className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-background-hover rounded-md transition-colors"
      aria-label="Open search"
    >
      <Icon name="MagnifyingGlass" size={18} />
      {!isMobile && showShortcutHint && (
        <span className="hidden md:flex items-center gap-1 text-xs">
          <kbd className="px-1.5 py-0.5 bg-background-muted border border-border-muted rounded text-xs">
            {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}
          </kbd>
          <kbd className="px-1.5 py-0.5 bg-background-muted border border-border-muted rounded text-xs">K</kbd>
        </span>
      )}
    </button>
  );

  // Headless mode: no trigger, controlled externally
  const isHeadless =
    trigger === null ||
    (isValidElement(trigger) && trigger.type === Fragment && !(trigger.props as { children?: ReactNode }).children);

  // Mobile: Full-screen tray
  if (isMobile) {
    return (
      <>
        {!isHeadless &&
          (trigger ? (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="appearance-none bg-transparent border-none p-0 cursor-pointer"
            >
              {trigger}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="appearance-none bg-transparent border-none p-0 cursor-pointer"
            >
              {defaultTrigger}
            </button>
          ))}

        <Tray
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          hideCloseIcon={false}
          contentPadding={false}
          maxHeight="90vh"
        >
          <div className="flex flex-col h-full">
            {/* Search Input */}
            {showInput && (
              <div className="p-4 border-b border-border-muted flex-shrink-0">
                <TextField
                  value={value}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  showSearchIcon={true}
                  isClearable={!!value}
                  onClear={() => onChange?.("")}
                  reserveErrorSpace={false}
                  className={className}
                  autoFocus
                />
              </div>
            )}

            {/* Results */}
            <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
          </div>
        </Tray>
      </>
    );
  }

  // Desktop: Popover
  if (isHeadless) {
    // Headless mode on desktop: return null (use GlobalSearch component for headless patterns)
    // Note: Popover requires DialogTrigger context and cannot render standalone
    return null;
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {trigger || defaultTrigger}
      <Popover
        placement="bottom"
        className="w-[600px] max-w-[90vw] bg-background-surface shadow-xl border border-border-default rounded-lg overflow-hidden"
      >
        <div className="flex flex-col">
          {/* Search Input */}
          {showInput && (
            <div className="p-4 border-b border-border-muted">
              <TextField
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
                showSearchIcon={true}
                isClearable={!!value}
                onClear={() => onChange?.("")}
                reserveErrorSpace={false}
                className={className}
                autoFocus
              />
            </div>
          )}

          {/* Results */}
          <div className="max-h-[500px] overflow-hidden">{children}</div>
        </div>
      </Popover>
    </DialogTrigger>
  );
}
